import { useEffect, useMemo, useRef, useState } from "react";

interface Row {
  id: string;
}

// A row is kept this long after it disappears from the data before vanishing for
// good — so it is not yanked out from under the user's finger.
const LEAVING_MS = 200;

interface LeavingEntry<T> {
  task: T;
  // Position in the last rendered rows — the row comes back here instead of
  // jumping to the end of the list.
  index: number;
}

export function useLeavingRows<T extends Row>(rows: T[]): (T & { leaving?: boolean })[] {
  const prevRowsRef = useRef<T[]>(rows);
  // Each vanished row holds its own timer rather than a shared one: otherwise a later
  // disappearance of another row would cancel the first row's timer before it fired
  // (the effect re-runs on every rows change) — and the first row would be stuck forever.
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const [leaving, setLeaving] = useState<LeavingEntry<T>[]>([]);

  useEffect(() => {
    const prevRows = prevRowsRef.current;
    prevRowsRef.current = rows;

    const nextIds = new Set(rows.map((t) => t.id));

    // The row came back into the data before it faded out — clear the timer and stop
    // treating it as vanished.
    const returned = [...timersRef.current.keys()].filter((id) => nextIds.has(id));
    for (const id of returned) {
      clearTimeout(timersRef.current.get(id)!);
      timersRef.current.delete(id);
    }

    // The index is taken here, not inside the setLeaving updater: React may defer the
    // updater to a later render, by which point prevRowsRef already holds the new rows.
    const added: LeavingEntry<T>[] = prevRows.flatMap((task, index) => (nextIds.has(task.id) ? [] : [{ task, index }]));
    if (added.length === 0 && returned.length === 0) return;

    setLeaving((current) => {
      const withoutReturned = current.filter((entry) => !nextIds.has(entry.task.id));
      return [...withoutReturned, ...added];
    });

    for (const { task } of added) {
      const timer = setTimeout(() => {
        timersRef.current.delete(task.id);
        setLeaving((current) => current.filter((entry) => entry.task.id !== task.id));
      }, LEAVING_MS);
      timersRef.current.set(task.id, timer);
    }
  }, [rows]);

  // Clearing timers on unmount is a separate effect, not tied to rows, so it does
  // not fire (and cancel other rows' timers) on every change of the input data.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  return useMemo(() => withLeaving(rows, leaving), [rows, leaving]);
}

function withLeaving<T extends Row>(rows: T[], leaving: LeavingEntry<T>[]): (T & { leaving?: boolean })[] {
  const currentIds = new Set(rows.map((t) => t.id));
  const pending = leaving
    .filter((entry) => !currentIds.has(entry.task.id))
    .sort((a, b) => a.index - b.index);
  if (pending.length === 0) return rows;

  const result: (T & { leaving?: boolean })[] = [...rows];
  for (const entry of pending) {
    const at = Math.min(entry.index, result.length);
    result.splice(at, 0, { ...entry.task, leaving: true });
  }
  return result;
}
